import { useCallback, useEffect, useRef } from 'react'

import { useAuth } from './useAuth'
import { isMockAuth } from '@/lib/authMode'

/**
 * Hook for idle detection and automatic token refresh
 * Helps prevent NS_BINDING_ERROR when app has been idle
 */
export const useIdleTokenRefresh = (idleThresholdMinutes: number = 10) => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth()
  const useMockAuth = isMockAuth
  const lastActivityRef = useRef(Date.now())
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now()
  }, [])

  const refreshTokenIfIdle = useCallback(async () => {
    if (useMockAuth || !isAuthenticated) return

    const idleTime = Date.now() - lastActivityRef.current
    const idleThreshold = idleThresholdMinutes * 60 * 1000

    if (idleTime > idleThreshold) {
      try {
        await getAccessTokenSilently({
          timeoutInSeconds: 10,
        })
      } catch (error) {
        console.warn('Failed to refresh idle token:', error)
      }
    }
  }, [isAuthenticated, idleThresholdMinutes, useMockAuth, getAccessTokenSilently])

  useEffect(() => {
    if (useMockAuth) return

    // Set up activity listeners
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
    events.forEach((event) => {
      document.addEventListener(event, updateActivity, true)
    })

    // Set up periodic token refresh check
    refreshTimeoutRef.current = setInterval(refreshTokenIfIdle, 5 * 60 * 1000) // Check every 5 minutes

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, updateActivity, true)
      })
      if (refreshTimeoutRef.current) {
        clearInterval(refreshTimeoutRef.current)
      }
    }
  }, [updateActivity, refreshTokenIfIdle, useMockAuth])

  // Refresh token when window becomes visible (user returns to tab)
  useEffect(() => {
    if (useMockAuth) return

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshTokenIfIdle()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [refreshTokenIfIdle, useMockAuth])

  return { updateActivity, refreshTokenIfIdle }
}
